import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Calendar, Plus, ChevronLeft, ChevronRight, X, Save, Users, Filter, GraduationCap, UserCircle } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import IOSModal from '../ui/IOSModal';
import AppointmentActionsModal from './AppointmentActionsModal';
import ClientSelectionModal from './ClientSelectionModal';
import CourseSessionDetailModal from './courses/CourseSessionDetailModal';
import {
    gymCalendarCard,
    gymCalendarGridHeader,
    gymCalendarShell,
    gymCalendarTodayBtn,
    gymDarkBorder,
    gymDarkFilterInactive,
    gymDarkInput,
    gymDarkMutedText,
    gymModalHeader,
    gymModalInput,
    gymModalLabel,
    gymModalPanel,
} from './gymPageTheme';

export default function TrainerCalendarSimple() {
    const { getAuthenticatedApi, isGym, isTrainer, currentUser } = useAuth();
    const { t } = useTranslation();
    const [appointments, setAppointments] = useState([]);
    const [courseSessions, setCourseSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showActionsModal, setShowActionsModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCourseDetailModal, setShowCourseDetailModal] = useState(false);
    const [modalOrigin, setModalOrigin] = useState({ x: 0, y: 0 });
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [selectedCourseSession, setSelectedCourseSession] = useState(null);
    const [hoveredAppointment, setHoveredAppointment] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [selectedSlot, setSelectedSlot] = useState(null); // Slot selezionato per nuovo appuntamento
    
    // Filtri
    const [viewFilter, setViewFilter] = useState('all'); // 'all', 'appointments', 'courses'
    const [viewScope, setViewScope] = useState(isGym() ? 'gym' : 'trainer'); // 'gym' | 'trainer' | 'all'
    const [trainerFilter, setTrainerFilter] = useState('gym'); // 'all' per tutti, 'gym' per solo palestra, 'X' per trainer specifico
    const [trainerOptions, setTrainerOptions] = useState([]);
    const [loadingTrainers, setLoadingTrainers] = useState(false);

    const canManageAppointments = isGym() || (isTrainer() && viewScope === 'trainer');
    const canViewCourses = (isGym() && (trainerFilter === 'gym' || trainerFilter === 'all')) || (isTrainer() && (viewScope === 'gym' || viewScope === 'all'));

    useEffect(() => {
        fetchAppointments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, viewScope, trainerFilter]);
    
    useEffect(() => {
        if (isGym()) {
            loadTrainerOptions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!canManageAppointments) {
            setShowCreateModal(false);
            setShowEditModal(false);
        }
    }, [canManageAppointments]);

    const loadTrainerOptions = async () => {
        try {
            setLoadingTrainers(true);
            const api = getAuthenticatedApi();
            const response = await api.get('/trainer_calendar.php', {
                params: { action: 'trainers' }
            });
            setTrainerOptions(response.data.trainers || []);
        } catch (err) {
            console.error('Errore caricamento lista trainer:', err);
        } finally {
            setLoadingTrainers(false);
        }
    };

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            const api = getAuthenticatedApi();

            const params = {
                action: 'month',
                month: selectedDate.getMonth() + 1,
                year: selectedDate.getFullYear()
            };

            if (viewScope === 'gym') {
                params.scope = 'gym';
                if (isGym() && trainerFilter && trainerFilter !== 'gym' && trainerFilter !== 'all') {
                    params.trainer_id = trainerFilter;
                } else if (isGym() && trainerFilter === 'all') {
                    params.trainer_id = 'all';
                }
            } else if (viewScope === 'all') {
                // Trainer vuole vedere tutto: suoi appuntamenti + appuntamenti palestra
                params.scope = 'gym';
                params.trainer_id = 'all';
            } else {
                params.scope = 'trainer';
            }
            
            // Fetch appuntamenti
            const response = await api.get('/trainer_calendar.php', { params });
            const appointmentsData = response.data;
            
            let appointments = [];
            if (Array.isArray(appointmentsData)) {
                appointments = appointmentsData;
            } else if (appointmentsData.success && Array.isArray(appointmentsData.appointments)) {
                appointments = appointmentsData.appointments;
            }
            
            // Pulisci i dati per rimuovere eventuali "0" concatenati accidentalmente
            appointments = appointments.map(apt => ({
                ...apt,
                title: String(apt.title || '').replace(/^0+/, '').trim(),
                time: String(apt.time || '').replace(/0+$/, '').replace(/^0+/, '').trim(),
                // Assicurati che is_course sia un booleano - solo per corsi
                is_course: apt.is_course === true || (apt.course_id !== null && apt.course_id !== undefined)
            }));
            
            // Fetch sessioni corso (palestra o trainer in vista palestra)
            let courses = [];
            if (canViewCourses) {
                try {
                    const startOfWeek = new Date(selectedDate);
                    const day = startOfWeek.getDay();
                    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
                    startOfWeek.setDate(diff);
                    const month = startOfWeek.toISOString().slice(0, 7);
                    const coursesResponse = await api.get(`/gym_courses.php?action=list_sessions&month=${month}`);
                    if (coursesResponse.data.success && Array.isArray(coursesResponse.data.sessions)) {
                        courses = coursesResponse.data.sessions.map(session => ({
                            ...session,
                            start_datetime: session.session_date + ' ' + session.start_time,
                            end_datetime: session.session_date + ' ' + session.end_time,
                            time: session.start_time?.slice(0, 5),
                            is_course: true,
                            event_type: 'course',
                            title: session.course_title || session.title,
                            course_id: session.course_id || session.id
                        }));
                    }
                } catch (courseErr) {
                    console.error('Errore caricamento corsi:', courseErr);
                }
            }
            
            setAppointments(appointments);
            setCourseSessions(courses);
            setError(null);
        } catch (err) {
            console.error('Errore caricamento calendario:', err);
            setError(t('gym.calendar.errorLoading'));
            setAppointments([]);
            setCourseSessions([]);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
            </div>
        );
    }

    const hours = Array.from({ length: 14 }, (_, i) => i + 8); // 8:00 - 21:00
    
    // Funzione per tradurre i giorni della settimana
    const getDayName = (date) => {
        const dayNames = {
            'Mon': t('gym.calendar.days.monday'),
            'Tue': t('gym.calendar.days.tuesday'),
            'Wed': t('gym.calendar.days.wednesday'),
            'Thu': t('gym.calendar.days.thursday'),
            'Fri': t('gym.calendar.days.friday'),
            'Sat': t('gym.calendar.days.saturday'),
            'Sun': t('gym.calendar.days.sunday')
        };
        const shortName = date.toLocaleDateString('en-US', { weekday: 'short' });
        return dayNames[shortName] || shortName;
    };
    
    // Calcola settimana (Lun-Sab)
    const startOfWeek = new Date(selectedDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);
    
    const weekDays = [];
    for (let i = 0; i < 6; i++) { // Solo 6 giorni
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        weekDays.push(date);
    }

    const formatHour = (dateTime) => {
        try {
            return new Date(dateTime).toLocaleTimeString('it-IT', {
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (_) {
            return '';
        }
    };

    const getAppointmentsForDateTime = (date, hour) => {
        // Usa metodi locali invece di toISOString() per evitare problemi con fuso orario
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        
        const baseAppointments = appointments.filter(ev => !ev.course_id && !ev.is_course);
        const inlineCourseEvents = appointments.filter(ev => ev.course_id || ev.is_course);
        const combinedCourseEvents = canViewCourses ? [...inlineCourseEvents, ...courseSessions] : [];
        
        let allEvents = [];
        if (viewFilter === 'appointments') {
            allEvents = baseAppointments;
        } else if (viewFilter === 'courses') {
            allEvents = combinedCourseEvents;
        } else {
            allEvents = [...baseAppointments, ...combinedCourseEvents];
        }
        
        const filteredEvents = allEvents.filter(apt => {
            try {
                const aptStart = new Date(apt.start_datetime);
                const aptEnd = new Date(apt.end_datetime);
                // Usa metodi locali invece di toISOString() per evitare problemi con fuso orario
                const aptDateStr = `${aptStart.getFullYear()}-${String(aptStart.getMonth() + 1).padStart(2, '0')}-${String(aptStart.getDate()).padStart(2, '0')}`;
                const slotStart = new Date(date);
                slotStart.setHours(hour, 0, 0, 0);
                const slotEnd = new Date(date);
                slotEnd.setHours(hour + 1, 0, 0, 0);
                
                return aptDateStr === dateStr && aptStart < slotEnd && aptEnd > slotStart;
            } catch (err) {
                console.error('Errore nel filtro eventi:', err, apt);
                return false;
            }
        });
        
        return filteredEvents;
    };

    const handleMouseEnter = (appointment, event) => {
        setHoveredAppointment(appointment);
        setTooltipPosition({
            x: event.clientX + 10,
            y: event.clientY - 10
        });
    };

    const handleMouseLeave = () => {
        setHoveredAppointment(null);
    };

    return (
        <div className={gymCalendarShell}>
            {/* Header - Fisso in alto */}
            <div className="flex-shrink-0">
                <div className="px-4 py-3 w-[95%] mx-auto">
                {/* Header */}
                <div className="mb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Calendar className="text-indigo-600" size={24} />
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {t('gym.calendar.title')}
                                </h1>
                                <p className={`text-sm text-gray-600 ${gymDarkMutedText}`}>
                                    {t('gym.calendar.description')}
                                </p>
                            </div>
                        </div>
                        {canManageAppointments && (
                            <button
                                onClick={(e) => {
                                    if (!canManageAppointments) return;
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setModalOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                                    setShowCreateModal(true);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                {t('gym.calendar.newAppointment')}
                            </button>
                        )}
                    </div>
                    
                    {/* Filtri - Solo per GYM */}
                    {isGym() && (
                        <div className="mt-2 flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <Filter size={18} className={`text-gray-500 ${gymDarkMutedText}`} />
                                <span className={`text-sm text-gray-600 ${gymDarkMutedText} mr-2`}>Mostra:</span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setViewFilter('all')}
                                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                                            viewFilter === 'all'
                                                ? 'bg-indigo-600 text-white'
                                                : gymDarkFilterInactive
                                        }`}
                                    >
                                        {t('gym.calendar.filters.showAll')}
                                    </button>
                                    <button
                                        onClick={() => setViewFilter('appointments')}
                                        className={`px-3 py-1 rounded-lg text-sm transition-colors flex items-center gap-1 ${
                                            viewFilter === 'appointments'
                                                ? 'bg-blue-600 text-white'
                                                : gymDarkFilterInactive
                                        }`}
                                    >
                                        <UserCircle size={14} />
                                        {t('gym.calendar.filters.showAppointments')}
                                    </button>
                                    <button
                                        onClick={() => setViewFilter('courses')}
                                        className={`px-3 py-1 rounded-lg text-sm transition-colors flex items-center gap-1 ${
                                            viewFilter === 'courses'
                                                ? 'bg-green-600 text-white'
                                                : gymDarkFilterInactive
                                        }`}
                                    >
                                        <GraduationCap size={14} />
                                        {t('gym.calendar.filters.showCourses')}
                                    </button>
                                </div>
                            </div>
                            
                            {/* Selettore trainer per palestra */}
                            <div className="flex items-center gap-3">
                                <span className={`text-sm text-gray-600 ${gymDarkMutedText}`}>Vista:</span>
                                <select
                                    value={trainerFilter}
                                    onChange={(e) => setTrainerFilter(e.target.value)}
                                    className={`px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500 ${gymDarkInput}`}
                                >
                                    <option value="all">{t('gym.calendar.filters.showAll')}</option>
                                    <option value="gym">{t('gym.calendar.filters.gymView')}</option>
                                    {trainerOptions.map((trainer) => (
                                        <option key={trainer.id} value={String(trainer.id)}>
                                            {trainer.name || `Trainer #${trainer.id}`}
                                        </option>
                                    ))}
                                </select>
                                {loadingTrainers && (
                                    <span className={`text-xs text-gray-500 ${gymDarkMutedText}`}>
                                        {t('common.loading')}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Toggle vista per trainer */}
                    {isTrainer() && (
                        <div className="mt-2 flex flex-col gap-2">
                            <div className="flex items-center gap-3">
                                <span className={`text-sm text-gray-600 ${gymDarkMutedText}`}>Vista calendario:</span>
                                <select
                                    value={viewScope}
                                    onChange={(e) => setViewScope(e.target.value)}
                                    className={`px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-indigo-500 ${gymDarkInput}`}
                                >
                                    <option value="trainer">{t('gym.calendar.filters.mySchedule')}</option>
                                    <option value="gym">{t('gym.calendar.filters.gymView')}</option>
                                    <option value="all">{t('gym.calendar.filters.showAll')}</option>
                                </select>
                            </div>
                            {viewScope === 'gym' && (
                                <p className={`text-xs text-gray-500 ${gymDarkMutedText}`}>
                                    Modalità palestra in sola lettura
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Navigazione */}
                <div className={`${gymCalendarCard} p-3 mb-3`}>
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => {
                                    const newDate = new Date(selectedDate);
                                    newDate.setDate(newDate.getDate() - 7);
                                    setSelectedDate(newDate);
                                }}
                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t('gym.calendar.weekOf')} {selectedDate.toLocaleDateString('it-IT')}
                            </h2>
                            
                            <button
                                onClick={() => {
                                    const newDate = new Date(selectedDate);
                                    newDate.setDate(newDate.getDate() + 7);
                                    setSelectedDate(newDate);
                                }}
                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                        
                        <button
                            onClick={() => setSelectedDate(new Date())}
                            className={gymCalendarTodayBtn}
                        >
                            {t('gym.calendar.today')}
                        </button>
                    </div>
                </div>
                </div>
            </div>

            {/* Contenuto Scrollabile */}
            <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-2 w-[95%] mx-auto">
                {/* Error */}
                {error && (
                    <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                )}

                {/* Calendario */}
                <div className={`${gymCalendarCard} overflow-hidden`}>
                    <div className="overflow-x-auto">
                        {/* Header giorni - Fisso */}
                        <div className="min-w-[900px]" style={{ display: 'grid', gridTemplateColumns: '60px repeat(6, 1fr)' }}>
                            <div className={`p-2 text-center text-xs font-medium text-gray-600 ${gymCalendarGridHeader}`}>
                                {t('gym.calendar.time')}
                            </div>
                            {weekDays.map((date, index) => (
                                <div key={index} className={`p-2 text-center border-l border-gray-200 border-b ${gymCalendarGridHeader}`}>
                                    <div className="font-medium text-sm text-gray-900 dark:text-white">
                                        {getDayName(date)}
                                    </div>
                                    <div className={`text-xs text-gray-500 ${gymDarkMutedText}`}>
                                        {date.getDate()}/{date.getMonth() + 1}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Griglia ore - Scrollabile */}
                        <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
                            <div className="min-w-[900px]" style={{ display: 'grid', gridTemplateColumns: '60px repeat(6, 1fr)' }}>
                                {hours.map((hour) => (
                                    <div key={hour} className="contents">
                                        {/* Ora */}
                                        <div className={`p-1 text-center text-xs font-medium text-gray-500 border-r border-b flex items-center justify-center h-[70px] ${gymCalendarGridHeader} border-gray-100 ${gymDarkBorder}`}>
                                            {hour.toString().padStart(2, '0')}:00
                                        </div>
                                        
                                        {/* Giorni */}
                                        {weekDays.map((date, dayIndex) => {
                                            const dayAppointments = getAppointmentsForDateTime(date, hour);
                                            
                                            return (
                                                <div 
                                                    key={dayIndex} 
                                                    className={`border-l border-gray-200 border-b border-gray-100 transition-colors relative h-[70px] ${gymDarkBorder} ${canManageAppointments ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40' : 'cursor-default'}`}
                                                    onClick={() => {
                                                        if (dayAppointments.length > 0) {
                                                            setSelectedAppointment(dayAppointments[0]);
                                                            setShowActionsModal(true);
                                                        } else {
                                                            if (!canManageAppointments) return;
                                                            // Crea nuovo appuntamento per questo slot
                                                            setSelectedSlot({
                                                                date: date,
                                                                hour: hour
                                                            });
                                                            setShowCreateModal(true);
                                                        }
                                                    }}
                                                >
                                                    {/* Appuntamenti semplici */}
                                                    {(() => {
                                                        // Mostra solo gli appuntamenti che INIZIANO in quest'ora specifica
                                                        const hourAppointments = dayAppointments.filter(apt => {
                                                            const aptStart = new Date(apt.start_datetime);
                                                            return aptStart.getHours() === hour;
                                                        });
                                                        
                                                        // Rimuovi duplicati basati sull'ID
                                                        const uniqueAppointments = hourAppointments.filter((apt, index, array) => {
                                                            return array.findIndex(a => a.id === apt.id) === index;
                                                        });
                                                        
                                                        
                                                        const totalAppointments = uniqueAppointments.length;
                                                        
                                                        return uniqueAppointments.map((apt, index) => {
                                                            // Layout con colonne per appuntamenti sovrapposti
                                                            let width, left, top;
                                                            
                                                            const startTime = new Date(apt.start_datetime);
                                                            
                                                            // Calcola l'offset verticale basato sui minuti
                                                            const minutesOffset = startTime.getMinutes();
                                                            const slotHeight = 70; // ✅ Ancoraggio esatto allo slot
                                                            const topOffset = (minutesOffset / 60) * slotHeight;
                                                            
                                                            if (totalAppointments === 1) {
                                                                // Un solo appuntamento: occupa tutta la larghezza
                                                                width = '95%';
                                                                left = '2.5%';
                                                                top = `${2 + topOffset}px`;
                                                            } else {
                                                                // Sistema dinamico: 2-3 colonne in base al numero di appuntamenti
                                                                let maxColumns;
                                                                
                                                                if (totalAppointments <= 3) {
                                                                    maxColumns = totalAppointments; // 1, 2 o 3 colonne
                                                                } else {
                                                                    maxColumns = 3; // Massimo 3 colonne
                                                                }
                                                                
                                                                const columnWidth = 95 / maxColumns;
                                                                const columnIndex = index % maxColumns;
                                                                
                                                                // Tutti gli appuntamenti nelle colonne, nessun impilamento
                                                                width = `${columnWidth}%`;
                                                                left = `${2.5 + (columnIndex * columnWidth)}%`;
                                                                top = `${2 + topOffset}px`;
                                                            }
                                                            
                                                            const endTime = new Date(apt.end_datetime);
                                                            const durationMinutes = (endTime - startTime) / (1000 * 60);
                                                            // Usa slotHeight già dichiarato sopra (riga 515)
                                                            const height = Math.max((durationMinutes / 60) * slotHeight - 4, 20); // -4px per margine visivo

                                                            // Colori diversi per tipo di evento
                                                            const getEventColor = (apt) => {
                                                                // Per corsi, usa il colore personalizzato
                                                                if (apt.is_course && apt.color) {
                                                                    return '';  // Useremo style inline
                                                                }
                                                                
                                                                // Per appuntamenti, usa colori predefiniti
                                                                switch (apt.event_type) {
                                                                    case 'training':
                                                                        return 'bg-blue-500 hover:bg-blue-600';
                                                                    case 'consultation':
                                                                        return 'bg-green-500 hover:bg-green-600';
                                                                    case 'evaluation':
                                                                        return 'bg-purple-500 hover:bg-purple-600';
                                                                    case 'other':
                                                                        return 'bg-orange-500 hover:bg-orange-600';
                                                                    default:
                                                                        return 'bg-indigo-500 hover:bg-indigo-600';
                                                                }
                                                            };

                                                            const colorClass = getEventColor(apt);
                                                            const customStyle = apt.is_course && apt.color ? {
                                                                backgroundColor: apt.color,
                                                                height: `${height}px`,
                                                                width: width,
                                                                left: left,
                                                                top: top,
                                                                zIndex: 10 + index
                                                            } : {
                                                                height: `${height}px`,
                                                                width: width,
                                                                left: left,
                                                                top: top,
                                                                zIndex: 10 + index
                                                            };
                                                            
                                                            return (
                                                                <div
                                                                    key={`${apt.id}-${hour}-${index}`}
                                                                    className={`${colorClass} text-white rounded-md shadow-md absolute cursor-pointer transition-all ${apt.is_course ? 'ring-2 ring-white/30' : ''}`}
                                                                    style={customStyle}
                                                                    onMouseEnter={(e) => handleMouseEnter(apt, e)}
                                                                    onMouseLeave={handleMouseLeave}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        
                                                                        // Distingui click su corso vs appuntamento
                                                                        if (apt.is_course) {
                                                                            setSelectedCourseSession(apt);
                                                                            setShowCourseDetailModal(true);
                                                                        } else {
                                                                            setSelectedAppointment(apt);
                                                                            setShowActionsModal(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className="p-1">
                                                                        <div className="font-semibold truncate text-[13px] leading-tight">
                                                                            {apt.title?.trim() || 'Evento'}
                                                                        </div>
                                                                        <div className="text-xs opacity-90">
                                                                            {formatHour(apt.start_datetime)}
                                                                            {apt.is_course === true && apt.current_participants !== undefined && (
                                                                                <span className="ml-1">
                                                                                    {apt.max_participants !== null && apt.max_participants !== undefined && apt.max_participants > 0
                                                                                        ? `(${apt.current_participants}/${apt.max_participants})`
                                                                                        : `(${apt.current_participants} iscritti)`
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                    })()}
                                                    
                                                    {/* Indicatore vuoto */}
                                                    {dayAppointments.length === 0 && (
                                                        <div className="h-full flex items-center justify-center opacity-0 hover:opacity-20 transition-opacity">
                                                            <Plus size={14} className="text-gray-400" />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal */}
                {showActionsModal && selectedAppointment && (
                    <AppointmentActionsModal
                        isOpen={showActionsModal}
                        appointment={selectedAppointment}
                        readOnly={!canManageAppointments}
                        onClose={() => {
                            setShowActionsModal(false);
                            setSelectedAppointment(null);
                        }}
                        onEdit={() => {
                            setShowActionsModal(false);
                            setShowEditModal(true);
                        }}
                        onDelete={async () => {
                            try {
                                const api = getAuthenticatedApi();
                                await api.delete(`/trainer_calendar.php?id=${selectedAppointment.id}`);
                                fetchAppointments();
                                setShowActionsModal(false);
                                setSelectedAppointment(null);
                            } catch (err) {
                                console.error('Errore cancellazione:', err);
                                toast.error('Errore nella cancellazione');
                            }
                        }}
                    />
                )}
                
                {/* Modal di modifica appuntamento */}
                {canManageAppointments && showEditModal && selectedAppointment && (
                    <EditAppointmentModal
                        isOpen={showEditModal}
                        appointment={selectedAppointment}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedAppointment(null);
                        }}
                        onSuccess={() => {
                            setShowEditModal(false);
                            setSelectedAppointment(null);
                            fetchAppointments();
                        }}
                        trainerOptions={trainerOptions}
                    />
                )}
                
                {/* Modal di creazione appuntamento */}
                {canManageAppointments && showCreateModal && (
                    <CreateAppointmentModal
                        isOpen={showCreateModal}
                        onClose={() => {
                            setShowCreateModal(false);
                            setSelectedSlot(null);
                        }}
                        onSuccess={() => {
                            setShowCreateModal(false);
                            setSelectedSlot(null);
                            fetchAppointments();
                        }}
                        initialDate={selectedSlot?.date}
                        initialHour={selectedSlot?.hour}
                        trainerOptions={trainerOptions}
                    />
                )}
                
                {/* Tooltip */}
                {hoveredAppointment && (
                    <div
                        className="fixed bg-gray-800 text-white p-3 rounded-lg shadow-lg z-50 pointer-events-none"
                        style={{
                            left: tooltipPosition.x,
                            top: tooltipPosition.y,
                            maxWidth: '300px'
                        }}
                    >
                        <div className="font-semibold text-sm mb-1">
                            {hoveredAppointment.title}
                        </div>
                        <div className="text-xs text-gray-300 mb-1">
                            📅 {new Date(hoveredAppointment.start_datetime).toLocaleDateString('it-IT')}
                        </div>
                        <div className="text-xs text-gray-300 mb-1">
                            🕐 {new Date(hoveredAppointment.start_datetime).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} - {new Date(hoveredAppointment.end_datetime).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="text-xs text-gray-300 mb-1">
                            🎯 {hoveredAppointment.event_type === 'course' ? '🎓 Corso' :
                                hoveredAppointment.event_type === 'training' ? 'Allenamento' : 
                                hoveredAppointment.event_type === 'consultation' ? 'Consulenza' :
                                hoveredAppointment.event_type === 'evaluation' ? 'Valutazione' : 'Altro'}
                        </div>
                        {hoveredAppointment.is_course && (
                            <div className="text-xs text-gray-300 mb-1">
                                👥 {hoveredAppointment.current_participants || 0}/{hoveredAppointment.max_participants || '∞'} iscritti
                            </div>
                        )}
                        {hoveredAppointment.client_name && (
                            <div className="text-xs text-gray-300 mb-1">
                                👤 {hoveredAppointment.client_name}
                            </div>
                        )}
                        {hoveredAppointment.description && (
                            <div className="text-xs text-gray-300">
                                📝 {hoveredAppointment.description}
                            </div>
                        )}
                    </div>
                )}
                
                {/* Modal Dettagli Corso */}
                {showCourseDetailModal && selectedCourseSession && (
                    <CourseSessionDetailModal
                        isOpen={showCourseDetailModal}
                        session={selectedCourseSession}
                        onClose={() => {
                            setShowCourseDetailModal(false);
                            setSelectedCourseSession(null);
                        }}
                        onSessionUpdated={fetchAppointments}
                    />
                )}
                </div>
            </div>
        </div>
    );
}

// Componente per la modifica degli appuntamenti
function EditAppointmentModal({ isOpen, appointment, onClose, onSuccess, trainerOptions = [] }) {
    const { getAuthenticatedApi, isGym } = useAuth();
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        title: '',
        date: '',
        start_time: '',
        end_time: '',
        client_id: '',
        client_name: '',
        description: '',
        event_type: 'training',
        trainer_id: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showClientModal, setShowClientModal] = useState(false);

    useEffect(() => {
        if (appointment) {
            const aptDate = new Date(appointment.start_datetime);
            const endDate = new Date(appointment.end_datetime);
            
            setFormData({
                title: appointment.title || '',
                date: aptDate.toISOString().split('T')[0],
                start_time: aptDate.toTimeString().slice(0, 5),
                end_time: endDate.toTimeString().slice(0, 5),
                client_id: appointment.client_id || '',
                client_name: appointment.client_name || '',
                description: appointment.description || '',
                event_type: appointment.event_type || 'training',
                trainer_id: appointment.trainer_id ? String(appointment.trainer_id) : ''
            });
        }
    }, [appointment]);

    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const api = getAuthenticatedApi();
            const appointmentData = {
                title: formData.title,
                start_datetime: `${formData.date} ${formData.start_time}:00`,
                end_datetime: `${formData.date} ${formData.end_time}:00`,
                client_id: formData.client_id || null,
                client_name: formData.client_name,
                description: formData.description,
                event_type: formData.event_type
            };

            // Aggiungi trainer_id solo se è gym e se è stato selezionato
            if (isGym() && formData.trainer_id) {
                appointmentData.trainer_id = parseInt(formData.trainer_id);
            }

            await api.put(`/trainer_calendar.php?id=${appointment.id}`, appointmentData);
            onSuccess();
        } catch (err) {
            console.error('Errore aggiornamento appuntamento:', err);
            setError('Errore nell\'aggiornamento dell\'appuntamento');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) setError(null);
    };

    const handleClientSelect = (client) => {
        setFormData(prev => ({
            ...prev,
            client_id: client.client_id,
            client_name: client.client_name
        }));
        setShowClientModal(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className={`${gymModalPanel} max-w-md`}>
                <div className={gymModalHeader}>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Modifica Appuntamento</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-800 dark:text-white/70 dark:hover:text-white"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {error && (
                    <div className="border-b border-red-200 bg-red-50 p-4 dark:border-red-800/60 dark:bg-red-900/30">
                        <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                    </div>
                )}

                <form onSubmit={handleUpdate} className="space-y-4 p-4">
                    <div>
                        <label className={gymModalLabel}>
                            {t('gym.calendar.form.title')}
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            className={gymModalInput}
                            required
                        />
                    </div>

                    <div>
                        <label className={gymModalLabel}>
                            Data
                        </label>
                        <input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                            className={gymModalInput}
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={gymModalLabel}>
                                {t('gym.calendar.form.startTime')}
                            </label>
                            <input
                                type="time"
                                name="start_time"
                                value={formData.start_time}
                                onChange={handleChange}
                                className={gymModalInput}
                                required
                            />
                        </div>
                        <div>
                            <label className={gymModalLabel}>
                                {t('gym.calendar.form.endTime')}
                            </label>
                            <input
                                type="time"
                                name="end_time"
                                value={formData.end_time}
                                onChange={handleChange}
                                className={gymModalInput}
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className={gymModalLabel}>
                            Cliente
                        </label>
                        <div className="flex space-x-2">
                            <input
                                type="text"
                                name="client_name"
                                value={formData.client_name}
                                onChange={handleChange}
                                placeholder="Seleziona un cliente..."
                                className={`${gymModalInput} flex-1`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowClientModal(true)}
                                className="flex items-center space-x-1 rounded-md bg-indigo-600 px-3 py-2 text-white hover:bg-indigo-700 "
                            >
                                <Users className="h-4 w-4" />
                                <span>Scegli</span>
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className={gymModalLabel}>
                            Tipo Evento
                        </label>
                        <select
                            name="event_type"
                            value={formData.event_type}
                            onChange={handleChange}
                            className={gymModalInput}
                        >
                            <option value="training">Allenamento</option>
                            <option value="consultation">Consulenza</option>
                            <option value="evaluation">Valutazione</option>
                            <option value="other">Altro</option>
                        </select>
                    </div>

                    {/* Trainer (solo per gym) */}
                    {isGym() && (
                        <div>
                            <label className={gymModalLabel}>
                                Trainer
                            </label>
                            <select
                                name="trainer_id"
                                value={formData.trainer_id}
                                onChange={handleChange}
                                className={gymModalInput}
                            >
                                <option value="">Nessun trainer (Palestra)</option>
                                {trainerOptions.map((trainer) => (
                                    <option key={trainer.id} value={trainer.id}>
                                        {trainer.name || `Trainer #${trainer.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className={gymModalLabel}>
                            {t('gym.calendar.form.description')}
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={3}
                            className={gymModalInput}
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:text-gray-900 dark:text-white/75 dark:hover:text-white"
                            disabled={loading}
                        >
                            {t('gym.calendar.form.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center space-x-2 rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50 "
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>{t('gym.calendar.form.saving')}</span>
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    <span>{t('gym.calendar.form.save')}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
            
            {/* Modal di selezione clienti */}
            <ClientSelectionModal
                isOpen={showClientModal}
                onClose={() => setShowClientModal(false)}
                onSelect={handleClientSelect}
            />
        </div>
    );
}

// Componente per la creazione di nuovi appuntamenti
function CreateAppointmentModal({ isOpen, onClose, onSuccess, initialDate, initialHour, trainerOptions = [] }) {
    const { getAuthenticatedApi, isGym } = useAuth();
    const { t } = useTranslation();
    
    // Calcola data e ora iniziali basate sullo slot selezionato
    const getInitialDateTime = () => {
        if (initialDate && initialHour !== undefined) {
            // ✅ FIX: Usa ora locale invece di UTC per evitare bug timezone
            const year = initialDate.getFullYear();
            const month = String(initialDate.getMonth() + 1).padStart(2, '0');
            const day = String(initialDate.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            
            const startTime = `${initialHour.toString().padStart(2, '0')}:00`;
            const endTime = `${(initialHour + 1).toString().padStart(2, '0')}:00`;
            return { date: dateStr, start_time: startTime, end_time: endTime };
        }
        
        // ✅ FIX: Anche per data corrente, usa ora locale
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        
        return { 
            date: `${year}-${month}-${day}`, 
            start_time: '09:00', 
            end_time: '10:00' 
        };
    };
    
    const [formData, setFormData] = useState({
        title: '',
        ...getInitialDateTime(),
        client_id: '',
        client_name: '',
        description: '',
        event_type: 'training',
        trainer_id: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showClientModal, setShowClientModal] = useState(false);
    
    // Aggiorna form quando cambiano initialDate o initialHour
    useEffect(() => {
        if (initialDate && initialHour !== undefined) {
            const { date, start_time, end_time } = getInitialDateTime();
            setFormData(prev => ({
                ...prev,
                date,
                start_time,
                end_time
            }));
        }
    }, [initialDate, initialHour]);

    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const api = getAuthenticatedApi();
            const appointmentData = {
                title: formData.title,
                start_datetime: `${formData.date} ${formData.start_time}:00`,
                end_datetime: `${formData.date} ${formData.end_time}:00`,
                client_id: formData.client_id || null,
                client_name: formData.client_name,
                description: formData.description,
                event_type: formData.event_type
            };

            // Aggiungi trainer_id solo se è gym e se è stato selezionato
            if (isGym() && formData.trainer_id) {
                appointmentData.trainer_id = parseInt(formData.trainer_id);
            }

            await api.post('/trainer_calendar.php', appointmentData);
            onSuccess();
        } catch (err) {
            console.error('Errore creazione appuntamento:', err);
            setError('Errore nella creazione dell\'appuntamento');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) setError(null);
    };

    const handleClientSelect = (client) => {
        setFormData(prev => ({
            ...prev,
            client_id: client.client_id,
            client_name: client.client_name
        }));
        setShowClientModal(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className={`${gymModalPanel} max-w-md`}>
                <div className={gymModalHeader}>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('gym.calendar.newAppointment')}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-800 dark:text-white/70 dark:hover:text-white"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {error && (
                    <div className="border-b border-red-200 bg-red-50 p-4 dark:border-red-800/60 dark:bg-red-900/30">
                        <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                    </div>
                )}

                <form onSubmit={handleCreate} className="space-y-4 p-4">
                    <div>
                        <label className={gymModalLabel}>
                            {t('gym.calendar.form.title')}
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            className={gymModalInput}
                            required
                        />
                    </div>

                    <div>
                        <label className={gymModalLabel}>
                            Data
                        </label>
                        <input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                            className={gymModalInput}
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={gymModalLabel}>
                                {t('gym.calendar.form.startTime')}
                            </label>
                            <input
                                type="time"
                                name="start_time"
                                value={formData.start_time}
                                onChange={handleChange}
                                className={gymModalInput}
                                required
                            />
                        </div>
                        <div>
                            <label className={gymModalLabel}>
                                {t('gym.calendar.form.endTime')}
                            </label>
                            <input
                                type="time"
                                name="end_time"
                                value={formData.end_time}
                                onChange={handleChange}
                                className={gymModalInput}
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className={gymModalLabel}>
                            Cliente
                        </label>
                        <div className="flex space-x-2">
                            <input
                                type="text"
                                name="client_name"
                                value={formData.client_name}
                                onChange={handleChange}
                                placeholder="Seleziona un cliente..."
                                className={`${gymModalInput} flex-1`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowClientModal(true)}
                                className="flex items-center space-x-1 rounded-md bg-indigo-600 px-3 py-2 text-white hover:bg-indigo-700 "
                            >
                                <Users className="h-4 w-4" />
                                <span>Scegli</span>
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className={gymModalLabel}>
                            Tipo Evento
                        </label>
                        <select
                            name="event_type"
                            value={formData.event_type}
                            onChange={handleChange}
                            className={gymModalInput}
                        >
                            <option value="training">Allenamento</option>
                            <option value="consultation">Consulenza</option>
                            <option value="evaluation">Valutazione</option>
                            <option value="other">Altro</option>
                        </select>
                    </div>

                    {/* Trainer (solo per gym) */}
                    {isGym() && (
                        <div>
                            <label className={gymModalLabel}>
                                Trainer
                            </label>
                            <select
                                name="trainer_id"
                                value={formData.trainer_id}
                                onChange={handleChange}
                                className={gymModalInput}
                            >
                                <option value="">Nessun trainer (Palestra)</option>
                                {trainerOptions.map((trainer) => (
                                    <option key={trainer.id} value={trainer.id}>
                                        {trainer.name || `Trainer #${trainer.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className={gymModalLabel}>
                            {t('gym.calendar.form.description')}
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={3}
                            className={gymModalInput}
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:text-gray-900 dark:text-white/75 dark:hover:text-white"
                            disabled={loading}
                        >
                            {t('gym.calendar.form.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center space-x-2 rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50 "
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Creando...</span>
                                </>
                            ) : (
                                <>
                                    <Plus className="w-4 h-4" />
                                    <span>Crea</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
            
            {/* Modal di selezione clienti */}
            <ClientSelectionModal
                isOpen={showClientModal}
                onClose={() => setShowClientModal(false)}
                onSelect={handleClientSelect}
            />
        </div>
    );
}
